/**
 * Model for a bout.
 */
import { v4 as UUID } from 'uuid';
import { Wrestler } from '@reg/shared/model/wrestler.model';
import { WeightClass } from '@reg/shared/model/weight-class.model';
import { BoutAttribute } from '@reg/shared/model/bout-attribute.model';
import get from 'lodash-es/get';

export class Bout {
    public id: string = null;
    public isNew: boolean = true;
    public bottomWrestler: Wrestler = null;
    public topWrestler: Wrestler = null;
    public winnerWrestlerId: string = null;
    public weightClass: WeightClass = null;
    public eventId: string = null;
    public result: string = null;
    public winType: string = null;
    public isTopBye: boolean = null;
    public isBottomBye: boolean = null;
    public dualId: string = null;
    public roundNameId: string = null;
    public attributes: BoutAttribute[] = [];

    public static getFields(): string[] {
        return [
            'bottomWrestler',
            'bottomWrestlerId',
            'topWrestler',
            'topWrestlerId',
            'winnerWrestlerId',
            'weightClass',
            'weightClassId',
            'eventId',
            'result',
            'winType',
            'isTopBye',
            'isBottomBye',
            'dualId',
            'roundNameId',
            'attributes',
        ];
    }

    constructor() {
        this.id = UUID();
    }

    public getLoserWrestler(): Wrestler {
        if (!this.winnerWrestlerId) {
            return null;
        }

        if (this.topWrestler &&
            this.winnerWrestlerId === this.topWrestler.id) {
            return this.bottomWrestler;
        }

        return this.topWrestler;
    }

    public getWinnerWrestler(): Wrestler {
        if (!this.winnerWrestlerId) {
            return null;
        }

        if (this.topWrestler &&
            this.winnerWrestlerId === this.topWrestler.id) {
            return this.topWrestler;
        }

        return this.bottomWrestler;
    }

    public clone(): Bout {
        const clone: Bout = new Bout();
        clone.id = this.id;
        clone.isNew = this.isNew;
        clone.bottomWrestler = this.bottomWrestler && this.bottomWrestler.clone();
        clone.topWrestler = this.topWrestler && this.topWrestler.clone();
        clone.winnerWrestlerId = this.winnerWrestlerId;
        clone.weightClass = this.weightClass && this.weightClass.clone();
        clone.eventId = this.eventId;
        clone.result = this.result;
        clone.winType = this.winType;
        clone.isTopBye = this.isTopBye;
        clone.isBottomBye = this.isBottomBye;
        clone.dualId = this.dualId;
        clone.roundNameId = this.roundNameId;
        clone.attributes = this.attributes && this.attributes.map((ba: BoutAttribute) => ba.clone());
        return clone;
    }

    public addUpdateAttribute(
        name: string,
        value: string | boolean,
        wrestlerId: string = null,
        comments: string = null,
    ): BoutAttribute {
        if (typeof (value) === 'boolean') {
            // variable is a boolean so convert it to a string
            value = value ? '1' : '0';
        }

        let boutAttribute: BoutAttribute = this.attributes.find(
            (ba: BoutAttribute) => ba.name === name && ba.wrestlerId === wrestlerId,
        );

        if (boutAttribute) {
            // Update the existing attribute
            boutAttribute.value = value;
            boutAttribute.comments = comments;
        } else {
            // Add a new attribute for the value
            boutAttribute = new BoutAttribute();
            boutAttribute.name = name;
            boutAttribute.value = value;
            boutAttribute.comments = comments;
            boutAttribute.wrestlerId = wrestlerId;
            boutAttribute.eventId = this.eventId;
            boutAttribute.boutId = this.id;
            this.attributes.push(boutAttribute);
        }

        return boutAttribute;
    }

    public hasAttributeWithValue(name: string, value: string, wrestlerId: string = null): boolean {
        return !!this.attributes.find(
            (ba: BoutAttribute) => ba.name === name && ba.value === value && ba.wrestlerId === wrestlerId,
        );
    }

    public getAttribute(name: string, wrestlerId: string = null): BoutAttribute {
        return this.attributes.find((ba: BoutAttribute) => ba.name === name && ba.wrestlerId === wrestlerId);
    }

    /**
     * Serialize to a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'bout';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.relationships = {};
        json.data.attributes.result = this.result;
        json.data.attributes.winType = this.winType;
        json.data.attributes.isTopBye = this.isTopBye;
        json.data.attributes.isBottomBye = this.isBottomBye;

        if (this.bottomWrestler) {
            json.data.relationships.bottomWrestler = {
                data: { type: 'wrestler', id: this.bottomWrestler.id },
            };
        }
        if (this.topWrestler) {
            json.data.relationships.topWrestler = {
                data: { type: 'wrestler', id: this.topWrestler.id },
            };
        }

        json.data.relationships.winnerWrestler = { data: { type: 'wrestler', id: this.winnerWrestlerId } };
        json.data.relationships.dual = { data: { type: 'dual', id: this.dualId } };
        json.data.relationships.roundName = { data: { type: 'roundName', id: this.roundNameId } };

        if (this.weightClass) {
            json.data.relationships.weightClass = {
                data: { type: 'weightClass', id: this.weightClass.id },
            };
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize from a json api format
     */
    public deserialize(json: any, included: any[] = null): Bout {
        const data: any = json.data || json;
        const attrs: any = data.attributes || {};
        const relationships: any = data.relationships || {};
        included = included || json.included;

        this.id = data.id;
        this.isNew = false;
        this.eventId = attrs.eventId;
        this.winnerWrestlerId = attrs.winnerWrestlerId;
        this.dualId = attrs.dualId;
        this.roundNameId = attrs.roundNameId;
        this.result = attrs.result;
        this.winType = attrs.winType;
        this.isTopBye = attrs.isTopBye === true;
        this.isBottomBye = attrs.isBottomBye === true;
        this.bottomWrestler = this.getWrestlerRelationship('bottomWrestler', attrs, relationships, included);
        this.topWrestler = this.getWrestlerRelationship('topWrestler', attrs, relationships, included);

        const weightClassLink: any = relationships.weightClass;
        if (weightClassLink &&
            included &&
            weightClassLink.data &&
            weightClassLink.data.id) {
            const weightClassId: string = weightClassLink.data.id;
            const weightClass: any[] =
                included.filter((i: any) => i.type === 'weightClass' && i.id === weightClassId);

            if (weightClass.length === 1) {
                this.weightClass = (new WeightClass()).deserialize(weightClass[0]);
            }
        } else if (attrs.weightClassId) {
            this.weightClass = new WeightClass(attrs.weightClassId);
        }

        const attributeList: any[] = get(data, 'relationships.attributes.data') || [];
        attributeList.forEach((x: any) => {
            this.attributes = this.attributes || [];
            const item: any = included.filter((i: any) => i.id === x.id)[0];

            if (item) {
                this.attributes.push((new BoutAttribute()).deserialize(item));
            }
        });

        return this;
    }

    private getWrestlerRelationship(
        relationshipName: string,
        attributes: any,
        relationships: any[],
        included: any[],
    ): Wrestler {
        const wrestlerLink: any = relationships[relationshipName];
        if (wrestlerLink &&
            included &&
            wrestlerLink.data &&
            wrestlerLink.data.id) {
            const wrestlerId: string = wrestlerLink.data.id;
            const wrestlers: any[] =
                included.filter((i: any) => i.type === 'wrestler' && i.id === wrestlerId);

            if (wrestlers.length === 1) {
                return (new Wrestler()).deserialize(wrestlers[0], included);
            }
        } else if (attributes[`${relationshipName}Id`]) {
            const wrestler: Wrestler = new Wrestler(attributes.eventId);
            wrestler.id = attributes[`${relationshipName}Id`];
            return wrestler;
        }

        return null;
    }
}
